import PropTypes from 'prop-types';
import {
    Card,
    Table,
    Row,
    Col,
    Typography,
    Tag,
    Divider,
    Space,
    Statistic
} from 'antd';
import {
    CalendarOutlined,
    CarOutlined,
    SafetyOutlined,
    CalculatorOutlined,
    FileTextOutlined,
    PercentageOutlined
} from '@ant-design/icons';
import { frenchDate } from '../../../../variables/functions';
import { displayNumbers } from '../../../../variables/numbersFunctions';

const { Title, Text } = Typography;

// ── Helpers tableau flotte (mêmes regroupements de colonnes que l'annexe flotte) ──
const PRIORITY_GAR = ['RC', 'IC', 'Vol', 'Incendie'];

const labelGarantie = (nom) => {
    const n = (nom || '').toUpperCase();
    if (n.startsWith('RC') || n.startsWith('SURPRIME')) return 'RC';
    if (n.includes('INDIVIDUEL') || n.includes('CONDUCTEUR')) return 'IC';
    if (n.startsWith('VOL')) return 'Vol';
    if (n.startsWith('INC')) return 'Incendie';
    return (nom || '').trim() || 'Autres';
};

const buildGarantieColumns = (engins) => {
    const seen = [];
    (engins || []).forEach((e) => (e?.compulsoryGuaranties || []).forEach((g) => {
        if (!(g?.primeNette > 0)) return;
        const l = labelGarantie(g.nom);
        if (l && !seen.includes(l)) seen.push(l);
    }));
    const prio = PRIORITY_GAR.filter((p) => seen.includes(p));
    const others = seen.filter((s) => !PRIORITY_GAR.includes(s));
    return [...prio, ...others];
};

const montantsParColonne = (engin) => {
    const map = {};
    (engin?.compulsoryGuaranties || []).forEach((g) => {
        if (!(g?.primeNette > 0)) return;
        const l = labelGarantie(g.nom);
        map[l] = (map[l] || 0) + (Number(g.primeNette) || 0);
    });
    return map;
};

const PreviewPrimes = (props) => {
    console.log(props?.preview);
    const guaranteesObligatoires = props?.preview?.compulsoryGuaranties?.filter(g => g.obligatoire);
    const guaranteesOptionnelles = props?.preview?.compulsoryGuaranties?.filter(g => !g.obligatoire);
    const isMultipleEngins = props?.preview?.engins && props?.preview?.engins?.length > 1;

    // ── Garanties effectivement impactées par la réduction (flotte / bonus-malus) ──
    // Éligibilité = flag de la garantie (paramétré) ; repli sur RC/SURPRIME si non renseigné.
    // « Impactée » = éligible ET le taux correspondant est non nul.
    const _isRcNom = (nom) => {
        const n = (nom || '').toUpperCase();
        return n.startsWith('RC') || n.startsWith('SURPRIME');
    };
    const _flotteTaux = Number(props?.preview?.flotte ?? 0);
    const _bonusTaux = Number(props?.preview?.bonusMalus ?? 0);
    const _allGuaranties = (props?.preview?.engins?.length
        ? props.preview.engins.flatMap((e) => e?.compulsoryGuaranties || [])
        : (props?.preview?.compulsoryGuaranties || []));
    const garantiesReduites = [...new Set(
        _allGuaranties
            .filter((g) => {
                const flotteEl = g?.flotte != null ? g.flotte : _isRcNom(g?.nom);
                const bonusEl = g?.bonus != null ? g.bonus : _isRcNom(g?.nom);
                return (flotteEl && _flotteTaux !== 0) || (bonusEl && _bonusTaux !== 0);
            })
            .map((g) => (g?.nom || '').trim())
            .filter(Boolean)
    )];

    // ── Données du tableau flotte (un seul tableau consolidé) ──
    const flotteEngins = isMultipleEngins ? (props?.preview?.engins || []) : [];
    const flotteGarColumns = buildGarantieColumns(flotteEngins);
    const flotteRows = flotteEngins.map((e, i) => {
        const m = montantsParColonne(e);
        const totalEngin = flotteGarColumns.reduce((s, c) => s + (m[c] || 0), 0) || (Number(e.primeNette) || 0);
        return {
            key: i,
            index: i + 1,
            marque: e.marque,
            puissance: e.puissance,
            numeroChassis: e.numeroChassis,
            immatriculation: e.immatriculation,
            valeurDeclaree: Number(e.valeurDeclaree) || 0,
            m,
            totalEngin,
        };
    });
    const flotteTotals = {};
    flotteGarColumns.forEach((c) => { flotteTotals[c] = flotteRows.reduce((s, r) => s + (r.m[c] || 0), 0); });
    const flotteTotalValeur = flotteRows.reduce((s, r) => s + r.valeurDeclaree, 0);
    const flotteGrandTotal = flotteRows.reduce((s, r) => s + r.totalEngin, 0);

    const flotteColumns = [
        { title: 'N°', dataIndex: 'index', key: 'index', width: 40, align: 'center' },
        { title: 'Immatriculation', dataIndex: 'immatriculation', key: 'immatriculation', ellipsis: true, render: (v) => v || '-' },
        { title: 'Marque', dataIndex: 'marque', key: 'marque', ellipsis: true, render: (v) => v || '-' },
        { title: 'Puiss.', dataIndex: 'puissance', key: 'puissance', width: 64, ellipsis: true, render: (v) => v || '-' },
        { title: 'Valeur Décl.', dataIndex: 'valeurDeclaree', key: 'valeurDeclaree', align: 'right', render: (v) => v ? displayNumbers(v) : '-' },
        ...flotteGarColumns.map((c) => ({
            title: c, key: `gar_${c}`, align: 'right',
            render: (_, r) => (r.m[c] ? displayNumbers(r.m[c]) : '-'),
        })),
        {
            title: 'Prime Nette', dataIndex: 'totalEngin', key: 'totalEngin', align: 'right',
            render: (v) => <Text strong style={{ color: '#1890ff' }}>{displayNumbers(v)}</Text>,
        },
    ];

    const obligatoiresColumns = [
        {
            title: 'Garantie',
            dataIndex: 'nom',
            key: 'nom',
            render: (text) => (
                <Space>
                    <Text strong>{text}</Text>
                    <Tag color="green">Obligatoire</Tag>
                </Space>
            ),
        },
        {
            title: 'Capital',
            dataIndex: 'capital',
            key: 'capital',
            align: 'right',
            render: (capital) => capital ? `${displayNumbers(capital)} F CFA` : '-',
        },
        {
            title: 'Prime Nette',
            dataIndex: 'primeNette',
            key: 'primeNette',
            align: 'right',
            render: (prime) => <Text strong style={{ color: '#1890ff' }}>{displayNumbers(prime)} F CFA</Text>,
        },
    ];

    const optionnellesColumns = [
        {
            title: 'Garantie',
            dataIndex: 'nom',
            key: 'nom',
            render: (text) => (
                <Space>
                    <Text strong>{text}</Text>
                    <Tag color="blue">Optionnelle</Tag>
                </Space>
            ),
        },
        {
            title: 'Capital',
            dataIndex: 'capital',
            key: 'capital',
            align: 'right',
            render: (capital) => capital ? `${displayNumbers(capital)} F CFA` : '-',
        },
        {
            title: 'Prime Nette',
            dataIndex: 'primeNette',
            key: 'primeNette',
            align: 'right',
            render: (prime) => <Text strong style={{ color: '#722ed1' }}>{displayNumbers(prime)} F CFA</Text>,
        },
    ];

    return (
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: 0, backgroundColor: '#f0f2f5', minHeight: 'auto' }}>
            <Card>
                {/* Header */}
                <div style={{
                    background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)',
                    color: 'white',
                    padding: 24,
                    marginBottom: 24,
                    borderRadius: 8
                }}>
                    <Space align="center" style={{ marginBottom: 6, marginTop: 0 }}>
                        <SafetyOutlined style={{ fontSize: 32 }} />
                        <Title level={2} style={{ color: 'white', margin: 0 }}>Aperçu des primes</Title>
                    </Space>
                    <br />
                    <Space size="large">
                        <Space>
                            <CalendarOutlined />
                            <Text style={{ color: 'rgba(255,255,255,0.85)' }}>
                                Date effet: {frenchDate(props?.preview?.dateEffet)}
                            </Text>
                        </Space>
                        <Space>
                            <CalendarOutlined />
                            <Text style={{ color: 'rgba(255,255,255,0.85)' }}>
                                Date écheance:  {frenchDate(props?.preview?.dateEcheance)}
                            </Text>
                        </Space>
                    </Space>
                </div>

                {/* Section Véhicules — tableau unique consolidé (façon annexe flotte) */}
                {isMultipleEngins && (
                    <Card title={
                        <Space>
                            <CarOutlined style={{ color: '#1890ff' }} />
                            <span>Véhicules Assurés ({flotteEngins.length})</span>
                        </Space>
                    } style={{ marginBottom: 24 }} bodyStyle={{ padding: 8 }}>
                        <div className="flotte-preview-table">
                            <style>{`
                                .flotte-preview-table .ant-table-cell { padding: 3px 6px !important; font-size: 12px !important; }
                                .flotte-preview-table .ant-table-thead > tr > th { padding: 5px 6px !important; font-size: 12px !important; font-weight: 600; }
                            `}</style>
                            <Table
                                size="small"
                                bordered
                                tableLayout="fixed"
                                columns={flotteColumns}
                                dataSource={flotteRows}
                                rowKey="key"
                                pagination={flotteEngins.length > 20 ? { pageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] } : false}
                                summary={() => (
                                <Table.Summary fixed>
                                    <Table.Summary.Row style={{ background: '#e6f7ff', fontWeight: 700 }}>
                                        <Table.Summary.Cell index={0} colSpan={4} align="center">Totaux ({flotteEngins.length})</Table.Summary.Cell>
                                        <Table.Summary.Cell index={4} align="right">{displayNumbers(flotteTotalValeur)}</Table.Summary.Cell>
                                        {flotteGarColumns.map((c, i) => (
                                            <Table.Summary.Cell key={c} index={5 + i} align="right">{displayNumbers(flotteTotals[c])}</Table.Summary.Cell>
                                        ))}
                                        <Table.Summary.Cell index={5 + flotteGarColumns.length} align="right">
                                            <Text strong style={{ color: '#1890ff' }}>{displayNumbers(flotteGrandTotal)}</Text>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                </Table.Summary>
                            )}
                            />
                        </div>
                    </Card>
                )}

                {/* Garanties Obligatoires */}
                {!isMultipleEngins && guaranteesObligatoires?.length > 0 && (
                    <Card
                        title={
                            <Space>
                                <SafetyOutlined style={{ color: '#52c41a' }} />
                                <span>Garanties Obligatoires</span>
                            </Space>
                        }
                        style={{ marginBottom: 24 }}
                    >
                        <Table
                            dataSource={guaranteesObligatoires?.filter(oc => oc?.primeNette > 0)}
                            columns={obligatoiresColumns}
                            pagination={false}
                            rowKey={(record, index) => index}
                        />
                    </Card>
                )}

                {/* Garanties Optionnelles */}
                {!isMultipleEngins && guaranteesOptionnelles?.length > 0 && (
                    <Card
                        title={
                            <Space>
                                <SafetyOutlined style={{ color: '#1890ff' }} />
                                <span>Garanties Optionnelles</span>
                            </Space>
                        }
                        style={{ marginBottom: 24 }}
                    >
                        <Table
                            dataSource={guaranteesOptionnelles?.filter(oc => oc?.primeNette > 0)}
                            columns={optionnellesColumns}
                            pagination={false}
                            rowKey={(record, index) => index}
                        />
                    </Card>
                )}
                {/* Réduction appliquée (flotte / bonus-malus) sur les garanties éligibles */}
                {(props?.preview?.rcDefaut !== props?.preview?.rcFinal) && (
                    <Card
                        title={
                            <Space>
                                <FileTextOutlined style={{ color: '#666' }} />
                                <span>Réduction appliquée</span>
                            </Space>
                        }
                        style={{ backgroundColor: '#fafafa' }}
                    >
                        <Row gutter={[24, 16]}>
                            <Col xs={24} md={6}>
                                <Statistic
                                    title="Prime réductible"
                                    value={displayNumbers(props?.preview?.rcDefaut)}
                                    suffix="F CFA"
                                    valueStyle={{ color: '#666', fontSize: 14 }}
                                />
                            </Col>
                            {props?.preview?.bonusMalus !== 0 && (
                                <Col xs={24} sm={6}>
                                    <Statistic
                                        title="Bonus/Malus"
                                        value={displayNumbers(Math.abs(props?.preview?.bonusMalus))}
                                        suffix="%"
                                        prefix={props?.preview?.bonusMalus > 0 ? '-' : '+'}
                                        valueStyle={{ color: props?.preview?.bonusMalus > 0 ? '#52c41a' : '#ff4d4f', fontSize: 14 }}
                                    />
                                </Col>
                            )}
                            {props?.preview?.flotte !== 0 && (
                                <Col xs={24} sm={6}>
                                    <Statistic
                                        title="Flotte"
                                        value={displayNumbers(Math.abs(props?.preview?.flotte))}
                                        suffix="%"
                                        prefix={'-'}
                                        valueStyle={{ color: '#52c41a', fontSize: 14 }}
                                    />
                                </Col>
                            )}
                            <Col xs={24} md={6}>
                                <Statistic
                                    title="Prime après réduction"
                                    value={displayNumbers(props?.preview?.rcFinal)}
                                    suffix="F CFA"
                                    valueStyle={{ color: '#1890ff', fontSize: 14 }}
                                />
                            </Col>
                        </Row>
                        {garantiesReduites.length > 0 && (
                            <div style={{ marginTop: 12 }}>
                                <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>
                                    Garanties concernées :
                                </Text>
                                {garantiesReduites.map((nom) => (
                                    <Tag key={nom} color="blue" style={{ marginBottom: 4 }}>
                                        {nom}
                                    </Tag>
                                ))}
                            </div>
                        )}
                    </Card>
                )}


                {/* Fractionnement */}
                {props?.preview?.primeNetteFractionnee > 0 && (
                    <Card
                        title={
                            <Space>
                                <PercentageOutlined style={{ color: '#fa8c16' }} />
                                <span>Fractionnement</span>
                            </Space>
                        }
                        style={{ marginBottom: 24, background: 'linear-gradient(135deg, #fff7e6 0%, #fffbe6 100%)' }}
                    >
                        <Row gutter={[24, 16]}>
                            <Col xs={24} md={12}>
                                <Statistic
                                    title="Taux de fractionnement"
                                    value={props?.preview?.fractionnement}
                                    suffix="%"
                                    valueStyle={{ color: '#fa8c16' }}
                                />
                            </Col>
                            <Col xs={24} md={12}>
                                <Statistic
                                    title="(Prime Nette + accessoires) Fractionnée"
                                    value={displayNumbers(
                                        (props?.preview?.primeNetteFractionnee || 0) +
                                        (props?.preview?.accessoiresFractionnee != null
                                            ? props?.preview?.accessoiresFractionnee
                                            : (props?.preview?.accessoires || 0) * (props?.preview?.fractionnement || 0) / 100)
                                    )}
                                    suffix="F CFA"
                                    valueStyle={{ color: '#52c41a' }}
                                />
                            </Col>
                        </Row>
                    </Card>
                )}


                {/* Détail des Primes */}
                <Card
                    title={
                        <Space>
                            <CalculatorOutlined style={{ color: '#722ed1' }} />
                            <span>Détail des Primes</span>
                        </Space>
                    }
                    style={{ marginBottom: 14, background: 'linear-gradient(135deg, #f9f0ff 0%, #fff0f6 100%)' }}
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={8}>
                            <Statistic
                                title="Prime Nette Annuelle"
                                value={displayNumbers(props?.preview?.primeNette)}
                                suffix="F CFA"
                                valueStyle={{ color: '#333', fontSize: 14 }}
                            />
                        </Col>
                        <Col xs={24} sm={8}>
                            <Statistic
                                title="Frais Accessoires"
                                value={displayNumbers(props?.preview?.accessoires)}
                                suffix="F CFA"
                                valueStyle={{ color: '#333', fontSize: 14 }}
                            />
                        </Col>
                        <Col xs={24} sm={8}>
                            <Statistic
                                title="Taxes à payer (6%)"
                                value={displayNumbers(props?.preview?.taxes)}
                                suffix="F CFA"
                                valueStyle={{ color: '#333', fontSize: 14 }}
                            />
                        </Col>

                    </Row>

                    <Divider />
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Title level={3} style={{ margin: 0, fontSize: 14 }}>Prime Totale à payer:</Title>
                        </Col>
                        <Col>
                            <Title level={2} style={{ color: '#722ed1', margin: 0, fontSize: 14 }}>
                                {displayNumbers(props?.preview?.primeTotale)} F CFA
                            </Title>
                        </Col>
                    </Row>
                </Card>


            </Card>
        </div>
    );
};

PreviewPrimes.propTypes = {
    preview: PropTypes.object
};

export default PreviewPrimes;